import { useState } from "react";
import { toast } from "sonner";
import { Wand2, Loader2, Plus, Crown } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { INTERVALS, COMBOS, KEY_INTERVALS, KEY_COMBOS, MOODS, nameFor, colorFor, stepLabel, approxSemis } from "@/lib/music";
import { Switch } from "@/components/ui/switch";

const selCls = "rounded-none bg-[#0f0d11] border-[#352d40] text-sm h-9";

const presetSets = (inKey) =>
  inKey
    ? {
        singles: KEY_INTERVALS.map((iv) => ({ id: iv.id, label: iv.label, short: iv.short, color: colorFor(approxSemis(iv.steps)), parts: [{ steps: iv.steps, name: `${iv.label} (in key)` }] })),
        combos: KEY_COMBOS.map((c) => ({ id: c.id, label: c.label, hint: c.steps.map(stepLabel).map((l) => l.split(" ")[0]).join(" "), parts: c.steps.map((st) => ({ steps: st, name: `${stepLabel(st)} (in key)` })) })),
      }
    : {
        singles: INTERVALS.map((iv) => ({ id: iv.id, label: iv.label, short: iv.short, color: colorFor(iv.semitones), parts: [{ semitones: iv.semitones, name: iv.label }] })),
        combos: COMBOS.map((c) => ({ id: c.id, label: c.label, hint: c.parts.map((p) => (p > 0 ? `+${p}` : p)).join(" "), parts: c.parts.map((st) => ({ semitones: st, name: nameFor(st) })) })),
      };

const IntervalEngine = ({ disabled, inKey, onHarmonize }) => {
  const { singles, combos } = presetSets(inKey);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-2">
        {singles.map((iv) => (
          <button key={iv.id} data-testid={`harmony-preset-${iv.id}`} disabled={disabled} onClick={() => onHarmonize(iv.parts)} className="btn-gothic justify-between !px-2.5 !tracking-[0.08em] normal-case">
            <span className="truncate">{iv.label}</span>
            <span className="font-bold" style={{ color: iv.color }}>{iv.short}</span>
          </button>
        ))}
      </div>
      <div>
        <p className="eyebrow mb-2">Choir stacks</p>
        <div className="space-y-2">
          {combos.map((c) => (
            <button key={c.id} data-testid={`harmony-preset-${c.id}`} disabled={disabled} onClick={() => onHarmonize(c.parts)} className="btn-gothic w-full justify-between normal-case !tracking-[0.08em]">
              <span>{c.label}</span>
              <span className="text-stone-500 truncate">{c.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Suggestion = ({ h, i, disabled, onApply }) => (
  <div className="border border-[#352d40] bg-[#0f0d11] p-3 rise" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`ai-suggestion-${i}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm text-stone-200 truncate">{h.name}</span>
      <span className="font-mono-vox text-xs font-bold" style={{ color: colorFor(h.semitones) }}>{h.semitones > 0 ? "+" : ""}{h.semitones}</span>
    </div>
    <p className="text-xs text-stone-400 mt-1 leading-relaxed">{h.rationale}</p>
    <button data-testid={`ai-apply-${i}`} disabled={disabled} onClick={() => onApply([h])} className="mt-2 text-[11px] font-mono-vox uppercase tracking-[0.18em] text-[#D99B43] hover:text-amber-200 inline-flex items-center gap-1 disabled:opacity-40">
      <Plus size={12} /> Raise voice
    </button>
  </div>
);

const AiGrimoire = ({ project, source, disabled, onHarmonize }) => {
  const [mood, setMood] = useState(MOODS[0]);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const summon = async () => {
    setBusy(true);
    try {
      const existing = project.tracks.filter((t) => t.file_id === source.file_id && t.id !== source.id).map((t) => t.semitones - source.semitones);
      const r = await api.suggestHarmony({ key: project.key, scale: project.scale, bpm: project.bpm, mood, prompt, source_name: source.name, existing });
      setResult(r);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-stone-400">Arranging in <span className="text-amber-300 font-mono-vox">{project.key} {project.scale}</span> at {project.bpm} BPM</p>
      <div className="space-y-1.5">
        <span className="eyebrow">Mood</span>
        <Select value={mood} onValueChange={setMood}>
          <SelectTrigger data-testid="harmony-ai-mood-select" className={selCls}><SelectValue /></SelectTrigger>
          <SelectContent>{MOODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <textarea data-testid="harmony-ai-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="e.g. wide and ghostly, with a low drone" className="w-full bg-[#0f0d11] border border-[#352d40] p-2.5 text-sm outline-none focus:border-[#D99B43] resize-none" />
      <button data-testid="btn-ai-summon" disabled={busy || disabled} onClick={summon} className="btn-crimson w-full justify-center">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} {busy ? "Consulting the grimoire…" : "Summon AI harmonies"}
      </button>
      {result && (
        <div className="space-y-2" data-testid="ai-results">
          <p className="font-cormorant italic text-base text-stone-300 leading-snug">{result.summary}</p>
          {result.harmonies.map((h, i) => <Suggestion key={i} h={h} i={i} disabled={disabled} onApply={onHarmonize} />)}
          {result.harmonies.length > 1 && (
            <button data-testid="ai-apply-all" disabled={disabled} onClick={() => onHarmonize(result.harmonies)} className="btn-gothic w-full justify-center">Raise all voices</button>
          )}
        </div>
      )}
    </div>
  );
};

export const HarmonyForge = ({ project, sourceId, setSourceId, busy, onHarmonize, isPro, onUpgrade }) => {
  const [inKey, setInKey] = useState(true);
  const applyAi = (hs) => onHarmonize(hs.map((h) => (inKey && h.steps ? { ...h, name: `${h.name} (in key)` } : { ...h, steps: undefined })));
  const sources = project.tracks.filter((t) => t.kind !== "backing");
  const source = sources.find((t) => t.id === sourceId);
  const disabled = !source || busy;
  return (
    <div data-testid="harmony-panel" className="space-y-5">
      <div>
        <p className="eyebrow text-[#D99B43]">⬥ Harmony Forge</p>
        <h3 className="font-gothic text-lg tracking-wider text-amber-500/90 mt-1">Multiply the voice</h3>
      </div>
      <div className="space-y-1.5">
        <span className="eyebrow">Source voice</span>
        <Select value={sourceId || ""} onValueChange={setSourceId} disabled={!sources.length}>
          <SelectTrigger data-testid="harmony-source-track-select" className={selCls}><SelectValue placeholder="No voices yet" /></SelectTrigger>
          <SelectContent>{sources.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <label className="flex items-center justify-between gap-3 border border-[#352d40] bg-[#0f0d11] px-3 py-2.5">
        <span>
          <span className="block text-sm text-stone-200">Follow the key</span>
          <span className="block text-[11px] text-stone-500">{inKey ? `Intervals adapt note-by-note to ${project.key} ${project.scale}` : "Fixed semitone shifts"}</span>
        </span>
        <Switch data-testid="harmony-inkey-switch" checked={inKey} onCheckedChange={setInKey} />
      </label>
      <Tabs defaultValue="interval">
        <TabsList className="w-full grid grid-cols-2 bg-[#0f0d11] rounded-none border border-[#352d40]">
          <TabsTrigger data-testid="tab-interval" value="interval" className="rounded-none font-mono-vox text-[11px] tracking-[0.15em] uppercase">Intervals</TabsTrigger>
          <TabsTrigger data-testid="tab-ai" value="ai" className="rounded-none font-mono-vox text-[11px] tracking-[0.15em] uppercase">AI Grimoire</TabsTrigger>
        </TabsList>
        <TabsContent value="interval" className="pt-4">
          <IntervalEngine disabled={disabled} inKey={inKey} onHarmonize={onHarmonize} />
        </TabsContent>
        <TabsContent value="ai" className="pt-4">
          {!isPro ? (
            <div className="stone-panel p-5 space-y-3" data-testid="ai-pro-lock">
              <p className="font-gothic uppercase tracking-wider text-amber-100/90 flex items-center gap-2"><Crown size={16} className="text-[#D99B43]" /> Pro grimoire</p>
              <p className="font-cormorant italic text-lg text-stone-400">AI-arranged harmony parts, tailored to your key, mood and direction, are part of Pro ($9/month).</p>
              <button data-testid="ai-upgrade-btn" onClick={onUpgrade} className="btn-crimson w-full justify-center"><Crown size={14} /> Unlock with Pro</button>
            </div>
          ) : source ? <AiGrimoire project={project} source={source} disabled={disabled} onHarmonize={applyAi} /> : <p className="font-cormorant italic text-stone-500 text-lg">Add a voice first.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
};
